/**
 * FILA DE EXECUÇÃO — intenção, não fato.
 *
 * O que está aqui é o que o motor pretende fazer; o que de fato saiu mora no
 * evento de mensagem. A distinção não é acadêmica: contar "programada" como
 * "enviada" infla o volume percebido e faz o operador acreditar que a
 * capacidade já foi consumida quando ainda não foi.
 *
 * Cancelar só existe para o que ainda não saiu. Depois do envio não há botão
 * que desfaça — e oferecer um seria mentira de interface.
 */
import { useEffect, useState } from "react";
import type { NumeroResumo, RespostaFila } from "@co/contracts";
import { api } from "../api.js";
import {
  Aviso,
  Botao,
  Carregando,
  Painel as Cartao,
  Selo,
  Tabela,
} from "../ui/base.js";
import { ErroAcao, ErroTela, useCarga } from "../ui/carga.js";
import { Filtros, LinhaVazia, Paginacao, classeFiltro } from "../ui/dados.js";
import { Modal } from "../ui/gaveta.js";
import { conta, dataHora, num, relativo } from "../ui/formato.js";
import { receber } from "../ui/alvo.js";
import {
  CANCELAVEIS,
  ESTADO_ATIVIDADE,
  ORDEM_ATIVIDADE,
  rotuloAtividade,
} from "../ui/rotulos.js";

const LIMITE = 50;

export function Fila(): JSX.Element {
  const [passagem] = useState(() => receber("fila"));

  const [estado, setEstado] = useState("");
  const [numeroId, setNumeroId] = useState(passagem?.numeroId ?? "");
  const [desloc, setDesloc] = useState(0);
  const [cancelar, setCancelar] = useState<string | null>(null);
  const [executando, setExecutando] = useState<string | null>(null);
  const [recado, setRecado] = useState<{ tom: "ok" | "crit"; texto: string } | null>(
    null,
  );



  const numeros = useNumeros();

  const chave = `fila|${estado}|${numeroId}|${desloc}`;
  const { carga, recarregar } = useCarga<RespostaFila>(
    () =>
      api.fila({
        estado: estado || undefined,
        numeroId: numeroId || undefined,
        limite: LIMITE,
        desloc,
      }),
    chave,
  );
  /**
   * Executa uma atividade agora. O erro devolvido pelo servidor é o motivo
   * REAL da recusa — janela fora do horário, número desconectado, destino
   * indisponível — e é isso que precisa aparecer, não um "falhou" genérico.
   */
  async function executar(id: string): Promise<void> {
    setExecutando(id);
    setRecado(null);
    try {
      const r = await api.executarAtividade(id);
      setRecado({ tom: "ok", texto: r.motivo });
      recarregar();
    } catch (e: unknown) {
      setRecado({
        tom: "crit",
        texto: e instanceof Error ? e.message : "Não consegui executar.",
      });
    } finally {
      setExecutando(null);
    }
  }

  const dados = carga.fase === "pronto" ? carga.dados : null;
  const temFiltro = Boolean(estado || numeroId);

  return (
    <div className="flex flex-col gap-5">
      <section
        aria-label="Resumo por estado"
        className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]"
      >
        {ORDEM_ATIVIDADE.map((e) => {
          const info = ESTADO_ATIVIDADE[e];
          const quantos = dados ? conta(dados.resumo, e) : 0;
          const ativo = estado === e;
          return (
            <button
              key={e}
              type="button"
              aria-pressed={ativo}
              onClick={() => {
                setEstado(ativo ? "" : e);
                setDesloc(0);
              }}
              className={`text-left rounded-[10px] border px-4 py-3 transition-colors ${
                ativo
                  ? "bg-marca-suave border-marca"
                  : "bg-superficie border-linha hover:bg-superficie-2 hover:border-linha-forte"
              }`}
            >
              <div className="text-[24px] leading-7 font-semibold tabular tracking-tight">
                {dados ? num(quantos) : "—"}
              </div>
              <div className="mt-1.5">
                <Selo tom={info.tom}>{info.rotulo}</Selo>
              </div>
            </button>
          );
        })}
      </section>

      <Cartao
        titulo="Fila de execução"
        dica="O aquecimento não depende de manter esta tela aberta"
        semCorpo
      >
        <div className="px-5 py-3 border-b border-linha">
          <Filtros>
            <select
              aria-label="Filtrar por estado da atividade"
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value);
                setDesloc(0);
              }}
              className={classeFiltro}
            >
              <option value="">Todos os estados</option>
              {ORDEM_ATIVIDADE.map((e) => (
                <option key={e} value={e}>
                  {ESTADO_ATIVIDADE[e].rotulo}
                </option>
              ))}
            </select>
            <select
              aria-label="Filtrar por número"
              value={numeroId}
              onChange={(e) => {
                setNumeroId(e.target.value);
                setDesloc(0);
              }}
              className={`${classeFiltro} min-w-[200px]`}
            >
              <option value="">Todos os números</option>
              {numeros.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.telefoneExibicao}
                  {n.apelido ? ` — ${n.apelido}` : ""}
                </option>
              ))}
            </select>
            {temFiltro && (
              <Botao
                pequeno
                tipo="fantasma"
                onClick={() => {
                  setEstado("");
                  setNumeroId("");
                  setDesloc(0);
                }}
              >
                Limpar filtros
              </Botao>
            )}
          </Filtros>
        </div>

        {recado && (
          <div className="px-5 pt-4">
            <Aviso tom={recado.tom}>{recado.texto}</Aviso>
          </div>
        )}

        {carga.fase === "carregando" && <Carregando />}
        {carga.fase === "erro" && (
          <div className="p-5">
            <ErroTela mensagem={carga.mensagem} aoTentar={recarregar} />
          </div>
        )}

        {dados && (
          <>
            <Tabela
              larguraMin={1080}
              colunas={[
                { chave: "n", rotulo: "Número" },
                { chave: "t", rotulo: "Atividade" },
                { chave: "d", rotulo: "Destino autorizado" },
                { chave: "p", rotulo: "Programada" },
                { chave: "e", rotulo: "Estado" },
                { chave: "x", rotulo: "Tent.", direita: true },
                { chave: "r", rotulo: "Resultado" },
                { chave: "a", rotulo: "Ação", direita: true },
              ]}
            >
              {dados.itens.length === 0 ? (
                <LinhaVazia colunas={8}>
                  {temFiltro
                    ? "Nenhuma atividade corresponde aos filtros."
                    : "Nenhuma atividade na fila. Ligue o motor para o plano voltar a andar."}
                </LinhaVazia>
              ) : (
                dados.itens.map((a) => {
                  const info = ESTADO_ATIVIDADE[a.estado];
                  const podeCancelar = CANCELAVEIS.includes(a.estado);
                  return (
                    <tr
                      key={a.id}
                      className="border-t border-linha align-top hover:bg-superficie-2"
                    >
                      <td className="px-5 py-2.5 text-[12.5px] tabular">
                        {a.numeroExibicao}
                      </td>
                      <td className="px-5 py-2.5 text-[12.5px]">
                        {rotuloAtividade(a.tipo)}
                        <span className="block text-[11.5px] text-tinta-3">
                          {a.etapaNome ?? "fora de etapa"}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-[12px] text-tinta-2">
                        {a.destinoRotulo ?? "—"}
                      </td>
                      <td className="px-5 py-2.5 text-[12px] tabular whitespace-nowrap">
                        {dataHora(a.agendadaPara)}
                        <span className="block text-[11px] text-tinta-3">
                          {a.executadaEm
                            ? `executada ${relativo(a.executadaEm)}`
                            : relativo(a.agendadaPara, "—")}
                        </span>
                      </td>
                      <td className="px-5 py-2.5">
                        <Selo tom={info.tom}>{info.rotulo}</Selo>
                      </td>
                      <td className="px-5 py-2.5 text-right text-[12.5px] tabular">
                        {num(a.tentativa)}
                      </td>
                      <td className="px-5 py-2.5 text-[12px] max-w-[280px]">
                        {a.erro ? (
                          <span className="text-crit-tinta">{a.erro}</span>
                        ) : (
                          <span className="text-tinta-3">
                            {a.executadaEm ? "sem erro registrado" : "aguardando"}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-right whitespace-nowrap">
                        <div className="flex gap-1.5 justify-end">
                          {(a.estado === "programada" || a.estado === "bloqueada") && (
                            <Botao
                              pequeno
                              tipo="fantasma"
                              desabilitado={executando === a.id}
                              titulo="Executa agora, sem esperar o motor. A janela do plano continua valendo."
                              onClick={() => void executar(a.id)}
                            >
                              {executando === a.id ? "Executando…" : "Executar"}
                            </Botao>
                          )}
                          {podeCancelar && (
                            <Botao
                              pequeno
                              tipo="fantasma"
                              onClick={() => setCancelar(a.id)}
                            >
                              Cancelar
                            </Botao>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </Tabela>
            <Paginacao pagina={dados.pagina} aoIr={setDesloc} />
          </>
        )}
      </Cartao>

      {cancelar && (
        <ModalCancelar
          id={cancelar}
          aoFechar={() => setCancelar(null)}
          aoCancelar={() => {
            setCancelar(null);
            recarregar();
          }}
        />
      )}
    </div>
  );
}

/** O seletor de número é conveniência; se falhar, a fila continua legível. */
function useNumeros(): NumeroResumo[] {
  const [itens, setItens] = useState<NumeroResumo[]>([]);
  useEffect(() => {
    let vivo = true;
    api
      .numeros({ limite: 200 })
      .then((r) => {
        if (vivo) setItens(r.itens);
      })
      .catch(() => {
        if (vivo) setItens([]);
      });
    return () => {
      vivo = false;
    };
  }, []);
  return itens;
}

function ModalCancelar({
  id,
  aoFechar,
  aoCancelar,
}: {
  id: string;
  aoFechar: () => void;
  aoCancelar: () => void;
}): JSX.Element {
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function confirmar(): Promise<void> {
    setErro(null);
    setEnviando(true);
    try {
      await api.cancelarAtividade(id);
      aoCancelar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível cancelar.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Cancelar a atividade programada"
      largura={480}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Manter programada
          </Botao>
          <Botao
            tipo="perigo"
            desabilitado={enviando}
            onClick={() => void confirmar()}
          >
            {enviando ? "Cancelando…" : "Cancelar atividade"}
          </Botao>
        </>
      }
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}
      <p className="text-[13px] text-tinta-2 m-0 leading-relaxed">
        A atividade sai da fila e não será executada. Cancelar uma atividade do
        plano de aquecimento atrasa a etapa do número: o mínimo de atividades da
        etapa continua valendo.
      </p>
    </Modal>
  );
}
